import { ref } from 'vue';
import { httpService } from '../services/httpService';
import { log, error as logError } from '../utils/DebugLogger';

import '../contracts/Origens_BuscaOrigensResponse';
import '../contracts/Origens_CreateOrigemResponse';
import '../contracts/Origens_DeleteOrigemResponse';
import '../contracts/Origens_GetOrigemResponse';
import '../contracts/Origens_ListaOrigensResponse';
import '../contracts/Origens_UpdateOrigemResponse';
import '../contracts/Origens_CreateOrigemInput';
import '../contracts/Origens_DeleteOrigemInput';
import '../contracts/Origens_UpdateOrigemInput';
import type { Origens_UpdateOrigemInput } from '../contracts/Origens_UpdateOrigemInput';
/**
 * Composable for Origens (Budget Origins) operations
 * Provides methods to interact with the Origens API
 */
export function useOrigens() {
  const isLoading = ref(false);
  const error = ref(null);

  /**
   * Fetch all origins for table listing
   */
  const fetchOrigens = async () => {
    log('useOrigens', 'fetchOrigens', 'Fetching all origins');
    isLoading.value = true;
    error.value = null;

    try {
      const response = await httpService.get('/index.php/config/origens/listaOrigens', { responseContract: 'Origens_ListaOrigensResponse' });
      log('useOrigens', 'fetchOrigens', 'Response', response);

      if (response.success) {
        return response.dados || [];
      } else {
        throw new Error(response.error || 'Erro ao buscar origens');
      }
    } catch (err) {
      logError('useOrigens', 'fetchOrigens', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Fetch single origin by ID
   */
  const fetchOrigem = async (origemId: number) => {
    log('useOrigens', 'fetchOrigem', 'Fetching origin', { origemId, type: typeof origemId });
    isLoading.value = true;
    error.value = null;

    try {
      const response = await httpService.get(`/index.php/config/origens/getOrigem/${origemId}`, { responseContract: 'Origens_GetOrigemResponse' });
      log('useOrigens', 'fetchOrigem', 'Response', response);

      if (response.success) {
        return response.dados;
      } else {
        const errorMsg = response.error || 'Erro ao buscar origem';
        logError('useOrigens', 'fetchOrigem', 'Backend returned error', { 
          response, 
          error: errorMsg 
        });
        throw new Error(errorMsg);
      }
    } catch (err) {
      logError('useOrigens', 'fetchOrigem', 'Error', {
        origemId,
        message: err.message,
        status: err.status,
        statusText: err.statusText,
        data: err.data,
        response: err.response
      });
      error.value = err.message;
      
      // Enhance error message if backend provided details
      if ((err as any).data) {
        const enhancedError = new Error((err as any).data.error || (err as any).data.Error || err.message);
        (enhancedError as any).originalError = err;
        (enhancedError as any).response = (err as any).response;
        (enhancedError as any).data = (err as any).data;
        throw enhancedError;
      }
      
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Create new origin
   */
  const createOrigem = async (data: any) => {
    log('useOrigens', 'createOrigem', 'Creating origin', data);
    isLoading.value = true;
    error.value = null;

    try {
      const response = await httpService.post('/index.php/config/origens/createOrigem', data, {
        responseContract: 'Origens_CreateOrigemResponse',
        inputContract: 'Origens_CreateOrigemInput'
      });
      log('useOrigens', 'createOrigem', 'Response', response);

      if (response.success) {
        return response;
      } else {
        throw new Error(response.error || 'Erro ao criar origem');
      }
    } catch (err) {
      logError('useOrigens', 'createOrigem', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Update existing origin
   */
  const updateOrigem = async (data: Origens_UpdateOrigemInput) => {
    log('useOrigens', 'updateOrigem', 'Updating origin', data);
    isLoading.value = true;
    error.value = null;

    try {
      const response = await httpService.post('/index.php/config/origens/updateOrigem', data, {
        responseContract: 'Origens_UpdateOrigemResponse',
        inputContract: 'Origens_UpdateOrigemInput'
      });
      log('useOrigens', 'updateOrigem', 'Response', response);

      if (response.success) {
        return response;
      } else {
        throw new Error(response.error || 'Erro ao atualizar origem');
      }
    } catch (err) {
      logError('useOrigens', 'updateOrigem', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Delete origin
   */
  const deleteOrigem = async (origemId: number) => {
    log('useOrigens', 'deleteOrigem', 'Deleting origin', origemId);
    isLoading.value = true;
    error.value = null;

    try {
      const response = await httpService.post('/index.php/config/origens/deleteOrigem', {
        origem_id: origemId
      }, {
        responseContract: 'Origens_DeleteOrigemResponse',
        inputContract: 'Origens_DeleteOrigemInput'
      });
      log('useOrigens', 'deleteOrigem', 'Response', response);

      if (response.success) {
        return response;
      } else {
        throw new Error(response.error || 'Erro ao deletar origem');
      }
    } catch (err) {
      logError('useOrigens', 'deleteOrigem', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Fetch origins formatted for select dropdown
   */
  const fetchOrigensSelect = async () => {
    log('useOrigens', 'fetchOrigensSelect', 'Fetching origins for select');
    isLoading.value = true;
    error.value = null;

    try {
      const response = await httpService.get('/index.php/config/origens/buscaOrigens', { responseContract: 'Origens_BuscaOrigensResponse' });
      log('useOrigens', 'fetchOrigensSelect', 'Response', response);

      if (response.success) {
        return response.dados || {};
      } else {
        throw new Error(response.error || 'Erro ao buscar origens para seleção');
      }
    } catch (err) {
      logError('useOrigens', 'fetchOrigensSelect', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  return {
    // State
    isLoading,
    error,

    // CRUD operations
    fetchOrigens,
    fetchOrigem,
    createOrigem,
    updateOrigem,
    deleteOrigem,

    // Helper methods
    fetchOrigensSelect
  };
}
